import {FunctionComponent} from "react";
import imageCover from "../../assets/Vyshniia.svg";
import {Button, Flex, Image, Text} from "@chakra-ui/react";
import {Form, Formik, useFormikContext} from "formik";
import {emailScheme} from "../../utilities/validationSchema";
import {auth} from "../../utilities/firebase";
import {sendPasswordResetEmail} from "firebase/auth";
import {showErrorToast, showInfoToast, showSuccessToast,} from "../../utilities/toasts";
import {ToastContainer} from "react-toastify";
import FormInput from "../../ui/components/FormInput/FormInput";
import Seo from "../../ui/components/Seo/Seo";
import {getRemainingCooldown, startCooldown, useCooldown,} from "../../hooks/useCooldown";

const RESET_COOLDOWN_SECONDS = 120;
const RESET_SENT_MESSAGE = "Лист надіслано, якщо акаунт із такою поштою існує.";
const getResetPasswordCooldownKey = (email: string) =>
  email ? `reset_password_cooldown:${email.trim().toLowerCase()}` : null;

interface ResetPasswordPageProps {}

const ResetPasswordForm: FunctionComponent = () => {
  const { values, isSubmitting } = useFormikContext<{ email: string }>();
  const cooldownKey = getResetPasswordCooldownKey(values.email);
  const { cooldown } = useCooldown(cooldownKey, RESET_COOLDOWN_SECONDS);

  return (
    <Form style={{ width: "100%" }}>
      <Flex
        gap={{
          base: "1.25rem",
          sm: "1.5rem",
          md: "1.75rem",
          xl: "2rem",
          "2xl": "2.5rem",
        }}
        flexDir="column"
        alignItems="center"
      >
        <FormInput
          name="email"
          placeholder="Введіть email адресу"
          label="Email"
          type="email"
        />

        <Button
          isLoading={isSubmitting}
          isDisabled={isSubmitting || cooldown > 0}
          minWidth={{ base: "100%", sm: "60%", lg: "42.5%" }}
          type="submit"
          height="fit-content"
          transition="all 0.3s"
          _hover={{ backgroundColor: "black" }}
          cursor={cooldown > 0 ? "not-allowed" : "pointer"}
          borderRadius={{ base: "0.5rem", sm: "0.35rem", xl: "0.5rem" }}
          backgroundColor="orange"
          width="fit-content"
          py={{
            base: "1.125rem",
            sm: "0.75rem",
            md: "1rem",
            xl: "1.125rem",
          }}
        >
          <Text
            lineHeight="1.25rem"
            fontWeight={400}
            color="white"
            size={{ base: "md", sm: "xs", md: "sm", xl: "md" }}
          >
            {cooldown > 0 ? `Надіслати знову (${cooldown}с)` : "Скинути пароль"}
          </Text>
        </Button>
      </Flex>
    </Form>
  );
};

const ResetPasswordPage: FunctionComponent<ResetPasswordPageProps> = () => {
  return (
    <>
      <Seo
        description={`Відновити пароль в Читанці`}
        title={"Відновити пароль"}
      />
      <Flex
        margin="0 auto"
        width={{ base: "100%", sm: "60%", "2xl": "42%" }}
        flexDir="column"
        alignItems="center"
      >
        <Image
          alt="Reset password"
          width={{
            base: "11.875rem",
            sm: "12.5rem",
            xl: "17.2rem",
          }}
          marginBottom={{ base: "1rem", md: "1.5rem", xl: "3rem" }}
          src={imageCover}
        />
        <Text
          marginBottom={{ base: "0.5rem", xl: "0.75rem" }}
          fontWeight={600}
          fontSize={{
            base: "1.75rem",
            sm: "1.5rem",
            md: "1.75rem",
            xl: "2.25rem",
          }}
        >
          Забули пароль?
        </Text>
        <Text
          marginBottom={{
            base: "1rem",
            sm: "1.5rem",
            md: "1.75rem",
            xl: "2rem",
          }}
          size={{
            base: "md",
            sm: "xs",
            md: "sm",
            xl: "md",
          }}
          textAlign="center"
          variant="secondary"
        >
          Введіть ваш email і ми надішлемо вам посилання для скидання паролю.
        </Text>
        <Formik
          validationSchema={emailScheme}
          validateOnBlur={false}
          validateOnChange={false}
          initialValues={{
            email: "",
          }}
          onSubmit={async (values, actions) => {
            const cooldownKey = getResetPasswordCooldownKey(values.email);
            if (getRemainingCooldown(cooldownKey) > 0) return;

            try {
              await sendPasswordResetEmail(auth, values.email, {
                url: `${process.env.REACT_APP_SITE_URL}/profile`,
              });
              showSuccessToast(RESET_SENT_MESSAGE);
              startCooldown(cooldownKey, RESET_COOLDOWN_SECONDS);
            } catch (error: any) {
              if (error?.code === "auth/user-not-found") {
                showSuccessToast(RESET_SENT_MESSAGE);
                startCooldown(cooldownKey, RESET_COOLDOWN_SECONDS);
              } else if (error?.code === "auth/too-many-requests") {
                showInfoToast("Забагато спроб. Спробуйте пізніше.");
                startCooldown(cooldownKey, RESET_COOLDOWN_SECONDS);
              } else if (error?.code === "auth/invalid-email") {
                showErrorToast("Хибний email, спробуйте ще раз.");
              } else {
                showErrorToast("Щось пішло не так, спробуйте ще раз.");
              }
            } finally {
              actions.setSubmitting(false);
            }
          }}
        >
          <ResetPasswordForm />
        </Formik>
        <ToastContainer />
      </Flex>
    </>
  );
};

export default ResetPasswordPage;
